import graphql from "babel-plugin-relay/macro";
import { atom, useSetAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useEffect } from "react";
import { useFragment } from "react-relay";

import { AgendaEventNode } from "../../types";
import { agendaEventNodesByIdAtom_events$key } from "./__generated__/agendaEventNodesByIdAtom_events.graphql";

/**
 * Stores a map of event ID to the GQL fragment key necessary for display
 */
export const agendaEventNodesByIdAtom = atom<AgendaEventNode[] | null>(null);

/**
 * Selector to allow us to access the GQL fragment key for an event by ID.
 */
export const getAgendaEventNodeKeyBySessionIdAtom = atomFamily(
  (sessionId: string | null) => {
    return atom((get) => {
      if (!sessionId) {
        return null;
      }
      const agendaEventNodesById = get(agendaEventNodesByIdAtom);
      return (
        agendaEventNodesById?.find((node) =>
          node.sessionIds.includes(sessionId)
        )?.node ?? null
      );
    });
  }
);

/**
 * Initialize the above agenda event nodes by id atom from a graphQL fragment.
 *
 * This is done so that we can grab all the event nodes 1 time at the top of an
 * agenda, and then access them on every slot -- without ballooning our response
 * payload via recursive GQL queries.
 */
export const useInitializeAgendaEventNodesByIdAtom = ({
  agendaRefs,
}: {
  agendaRefs: agendaEventNodesByIdAtom_events$key | null;
}) => {
  const setAgendaEventNodes = useSetAtom(agendaEventNodesByIdAtom);
  const agendas = useFragment(
    agendaEventNodesbyIdAtomFragmentString,
    agendaRefs
  );
  useEffect(() => {
    const agendaEventNodesById: AgendaEventNode[] =
      agendas?.flatMap((agenda) => {
        return (
          agenda?.events?.edges?.flatMap((edge) =>
            edge?.node?.id
              ? {
                  id: edge.node.id,
                  node: edge.node,
                  sessionIds:
                    edge.node.eventSessions?.edges?.flatMap((s) =>
                      s?.node?.id ? s.node.id : []
                    ) ?? [],
                }
              : []
          ) ?? []
        );
      }) ?? [];
    setAgendaEventNodes(agendaEventNodesById);
  }, [agendas, setAgendaEventNodes]);
};

export const agendaEventNodesbyIdAtomFragmentString = graphql`
  fragment agendaEventNodesByIdAtom_events on Agenda @relay(plural: true) {
    events {
      edges {
        node {
          id
          ...EventSlot_event
          eventSessions {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;
